import type {
  FirstSync,
  SyncReview,
  SyncReviewKind,
  SyncDestination,
  SyncExport,
  SyncFeed,
  SyncFetchResult,
  SyncProvider,
  SyncReader,
} from '../api/stays-sync-client';
import { clockTime, dateTimeIn, dayKeyIn, daysBetween, formatDayKey } from '../function-utils/building-time';
import { countOf } from '../function-utils/plural';

/**
 * CALENDAR SYNC — the words and states of the host's "Calendar sync" tab
 * (plan S5, screens §1). Pure: every clock is an input, nothing reads the
 * device's time by itself. Cases: stays-sync.cases.json.
 */

export const PROVIDERS: ReadonlyArray<{ key: SyncProvider; name: string }> = [
  { key: 'airbnb', name: 'Airbnb' },
  { key: 'booking', name: 'Booking.com' },
  { key: 'vrbo', name: 'Vrbo' },
  { key: 'other', name: 'Other' },
];

export const DESTINATIONS: ReadonlyArray<{ key: SyncDestination; name: string }> = [
  { key: 'airbnb', name: 'Airbnb' },
  { key: 'booking', name: 'Booking.com' },
  { key: 'vrbo', name: 'Vrbo' },
  { key: 'personal', name: 'Personal calendar' },
];

export const providerName = (p: SyncProvider | string): string =>
  (PROVIDERS.find(x => x.key === p) || { name: 'Other' }).name;

export const destinationName = (d: SyncDestination | string): string =>
  (DESTINATIONS.find(x => x.key === d) || { name: 'Calendar' }).name;

/** "Airbnb calendar" / "Other calendar (Holiday flat)" — the name every control and dialog uses. */
export const feedTitle = (f: Pick<SyncFeed, 'provider' | 'label'>): string =>
  f.provider === 'other' && f.label ? `${f.label} calendar` : `${providerName(f.provider)} calendar`;

/** The reason words of a failed fetch (screens §1). */
export const reasonWords = (result: SyncFetchResult | string | null | undefined, httpStatus?: number | null): string => {
  switch (result) {
    case 'timeout':
      return 'the site did not answer in time';
    case 'http_error':
      return httpStatus ? `the site answered ${httpStatus}` : 'the site answered with an error';
    case 'not_ical':
      return 'the link did not return a calendar';
    case 'too_large':
      return 'the calendar is larger than 2 MB';
    case 'blocked_address':
      return 'the link points to a private address';
    case 'dns_error':
      return "the site's address could not be found";
    case 'tls_error':
      return "the site's certificate was not accepted";
    case 'redirect_limit':
      return 'the link redirected too many times';
    case 'parse_error':
    case 'too_many_events':
      return 'the calendar could not be read';
    case 'no_network':
      return 'calendar checks are switched off on this server';
    default:
      return 'the calendar could not be checked';
  }
};

export type SyncPillTone = 'success' | 'warning' | 'alert' | 'neutral' | 'brand';

export interface SyncPill {
  tone: SyncPillTone;
  text: string;
  /** The instant behind the time in the text (data-at), else null. */
  at: string | null;
}

/** "14:05" on the reader's clock today, "7 Oct 2026, 14:05" on another day. */
export const whenText = (iso: string, zone: string, now: Date): string =>
  dayKeyIn(iso, zone) === dayKeyIn(now, zone) ? clockTime(iso, zone) : dateTimeIn(iso, zone);

/** The feed row's pill (screens §1). `syncing` wins: a sync is running from this screen. */
export const feedPill = (feed: SyncFeed, zone: string, now: Date, syncing = false): SyncPill => {
  const h = feed.health;
  if (syncing) return { tone: 'brand', text: 'Checking…', at: null };
  if (h.state === 'paused' || feed.paused) return { tone: 'neutral', text: 'Paused', at: null };
  if (h.state === 'failing') {
    return { tone: 'alert', text: `Failing — ${reasonWords(h.last_result, h.last_http_status)}`, at: h.last_fetch_at };
  }
  if (h.state === 'never' || !h.last_success_at) return { tone: 'neutral', text: 'Not checked yet', at: null };
  if (h.state === 'stale') {
    return { tone: 'warning', text: `Not synced since ${whenText(h.last_success_at, zone, now)}`, at: h.last_success_at };
  }

  return { tone: 'success', text: `Synced ${whenText(h.last_success_at, zone, now)}`, at: h.last_success_at };
};

/** The provider's "where to find the link" hint (the providers' public help wording; hints, not assertions). */
export const importHint = (p: SyncProvider): string => {
  switch (p) {
    case 'airbnb':
      return 'In Airbnb: Calendar → Availability → Connect calendars → Export calendar. Copy the link and paste it here.';
    case 'booking':
      return 'In the Booking.com extranet: Rates & availability → Sync calendars → Export. Copy the link.';
    case 'vrbo':
      return 'In Vrbo: Calendar → Import & export → Export calendar.';
    default:
      return 'Any https link to a .ics calendar.';
  }
};

/** Booking.com and Other mark a booked stay and a closed date the same way: the host chooses. */
export const asksAmbiguous = (p: SyncProvider): boolean => p === 'booking' || p === 'other';

/** "Airbnb calendar connected · 3 stays and 2 blocked periods found". */
export const connectedToast = (f: Pick<SyncFeed, 'provider' | 'label'>, s: Pick<FirstSync, 'stays_found' | 'blocks_found'>): string => {
  const found = [
    s.stays_found ? countOf(s.stays_found, 'stay') : '',
    s.blocks_found ? countOf(s.blocks_found, 'blocked period') : '',
  ].filter(Boolean);

  return `${feedTitle(f)} connected · ${found.length ? `${found.join(' and ')} found` : 'nothing booked yet'}`;
};

/** The visible reason of a refused "Sync now". */
export const retryWords = (seconds: number | null | undefined): string => {
  const s = Math.max(1, Math.ceil(Number(seconds) || 60));

  return `You can sync again in ${countOf(s, 'second')}`;
};

/** "2 h ago", "5 min ago", "just now", "3 days ago" — `now` is an input. */
export const agoText = (iso: string, now: Date): string => {
  const ms = now.getTime() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;

  return `${countOf(Math.floor(h / 24), 'day')} ago`;
};

export const readerName = (r: SyncReader | string | null | undefined): string => {
  switch (r) {
    case 'airbnb':
      return 'Airbnb';
    case 'booking':
      return 'Booking.com';
    case 'vrbo':
      return 'Vrbo';
    case 'google':
      return 'Google Calendar';
    case 'apple':
      return 'Apple Calendar';
    case 'microsoft':
      return 'Outlook';
    default:
      return 'a calendar app';
  }
};

/** The export row's state line. */
export const exportState = (x: SyncExport | null | undefined, now: Date): string => {
  if (!x) return 'No link yet';
  if (!x.last_read_at) return 'Link active · not read yet';

  return `Link active · last read ${agoText(x.last_read_at, now)} by ${readerName(x.last_reader)}`;
};

/** What to do with a link just made (shown once, with the link). */
export const exportHint = (d: SyncDestination): string => {
  switch (d) {
    case 'airbnb':
      return 'Paste this into Airbnb: Calendar → Availability → Connect calendars → Import calendar. You will only see this link once.';
    case 'booking':
      return 'Paste this into the Booking.com extranet: Rates & availability → Sync calendars → Import. You will only see this link once.';
    case 'vrbo':
      return 'Paste this into Vrbo: Calendar → Import & export → Import calendar. You will only see this link once.';
    default:
      return 'Add this to your own calendar app as a subscribed calendar. You will only see this link once.';
  }
};

/** The row's own hint, shown before any link exists. */
export const destinationHint = (d: SyncDestination): string | null =>
  d === 'personal'
    ? 'For your own phone or Google calendar. Do not give this one to a booking site: it includes that site’s own stays.'
    : d === 'booking'
      ? 'Booking.com may refuse calendar links from some sites. If it does, tell us.'
      : null;

/**
 * A refused connect / settings save → the words under the link field (or null
 * when the refusal belongs elsewhere). `fallback` is the server's own message.
 */
export const linkError = (
  code: string | null | undefined,
  { reason = null, httpStatus = null, expected = null }: { reason?: string | null; httpStatus?: number | null; expected?: string | null } = {}
): string | null => {
  switch (code) {
    case 'FEED_URL_INVALID':
      return 'Paste the full https link to the calendar.';
    case 'FEED_HOST_MISMATCH':
      return `This is not ${expected ? providerName(expected) : 'that booking site'}’s calendar link.`;
    case 'FEED_URL_IN_USE':
      return 'This calendar is already connected.';
    case 'FEED_NOT_ICAL':
      return 'The link did not return a calendar.';
    case 'FEED_TOO_LARGE':
      return 'The calendar is larger than 2 MB.';
    case 'FEED_FETCH_FAILED':
      return `The link could not be checked: ${reasonWords(reason, httpStatus)}.`;
    default:
      return null;
  }
};

// ── the review list (screens §2) ───────────────────────────────────────────

/** The kind's pill: "New stay", "Dates changed", "Clashes", "Missing from Airbnb", "Cancelled on Airbnb". */
export const kindPill = (kind: SyncReviewKind, provider: SyncProvider): { tone: SyncPillTone; text: string } => {
  const site = providerName(provider);
  switch (kind) {
    case 'new':
      return { tone: 'brand', text: 'New stay' };
    case 'date_update':
      return { tone: 'warning', text: 'Dates changed' };
    case 'conflict':
      return { tone: 'alert', text: 'Clashes' };
    case 'missing_from_feed':
      return { tone: 'warning', text: `Missing from ${site}` };
    default:
      return { tone: 'alert', text: `Cancelled on ${site}` };
  }
};

const WEEKDAY: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };

/** "Fri 12 Nov → Mon 15 Nov · 3 nights" — day keys printed as they are, never re-zoned. */
export const datesLine = (checkIn: string, checkOut: string): string => {
  const n = daysBetween(checkIn, checkOut);

  return `${formatDayKey(checkIn, WEEKDAY)} → ${formatDayKey(checkOut, WEEKDAY)} · ${countOf(n, 'night')}`;
};

/** "12 to 15 Nov" / "30 Nov to 2 Dec" — for the buttons' accessible names. */
export const shortRange = (checkIn: string, checkOut: string): string => {
  const sameMonth = checkIn.slice(0, 7) === checkOut.slice(0, 7);

  return sameMonth
    ? `${Number(checkIn.slice(8, 10))} to ${formatDayKey(checkOut)}`
    : `${formatDayKey(checkIn)} to ${formatDayKey(checkOut)}`;
};

/** A plain new stay: the only kind "Add all" may take (the server's batch rule). */
export const isPlainNew = (r: SyncReview): boolean =>
  r.kind === 'new' && r.state === 'pending' && r.proposed.as === 'reservation' && !r.proposed.restore && !r.conflicts.length;

/** A decided item's line in "Recent". */
export const decidedLine = (r: SyncReview): string => {
  switch (r.state) {
    case 'auto_approved':
      return 'Added automatically';
    case 'approved':
      return r.decided_by_name ? `Approved by ${r.decided_by_name}` : 'Approved';
    case 'dismissed':
      return r.decided_by_name ? `Ignored by ${r.decided_by_name}` : 'Ignored';
    case 'superseded':
      return 'Replaced by a newer change';
    default:
      return 'Waiting for you';
  }
};
